import { type ClassValue, clsx } from 'clsx';
import type { Timestamp } from 'firebase/firestore';
import { badgeClassName, type BadgeVariant } from '@/components/ui/badge';
import type { Locale } from './i18n';
import type { messages as MSG } from './i18n.messages';

type StatusKey = keyof typeof MSG;

export type TranslateFn = (
  key: keyof typeof MSG,
  vars?: Record<string, string | number>,
) => string;

export const cn = (...x: ClassValue[]) => clsx(x);

export function fmtBytes(b?: number): string {
  if (b == null) return '—';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.max(b, 1)) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export function fmtDate(
  ts?: Timestamp | Date | string | number | null,
  locale: Locale = 'fr',
): string {
  if (!ts) return '—';
  let d: Date;
  if (ts instanceof Date) d = ts;
  else if (typeof ts === 'string' || typeof ts === 'number') d = new Date(ts);
  else if ('toDate' in ts) d = ts.toDate();
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  const tag = locale === 'en' ? 'en-CA' : 'fr-CA';
  return d.toLocaleString(tag, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Relative time via i18n keys (`time.*`). Pass `t` and `locale` from `useT()`. */
export function fmtRelative(
  ts: Timestamp | Date | null | undefined,
  t: TranslateFn,
  locale: Locale,
): string {
  if (!ts) return '—';
  const d = ts instanceof Date ? ts : ts.toDate();
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return t('time.just_now');
  if (diff < 3600) return t('time.min_ago', { n: Math.floor(diff / 60) });
  if (diff < 86400) return t('time.h_ago', { n: Math.floor(diff / 3600) });
  if (diff < 86400 * 30) return t('time.d_ago', { n: Math.floor(diff / 86400) });
  return fmtDate(d, locale);
}

export function statusBadgeVariant(s?: string): BadgeVariant {
  switch (s) {
    case 'ready':
    case 'success':
    case 'accepted':
      return 'success';
    case 'processing':
    case 'uploading':
    case 'pending':
    case 'draft':
      return 'warning';
    case 'failed':
    case 'expired':
    case 'revoked':
      return 'danger';
    case 'submitted':
      return 'info';
    default:
      return 'neutral';
  }
}

export function statusColor(s?: string): string {
  return badgeClassName(statusBadgeVariant(s));
}

/**
 * Mapping from raw Firestore status strings to i18n keys.
 * Use with `t(STATUS_KEY[status])` for localized labels.
 */
export const STATUS_KEY: Record<string, StatusKey> = {
  uploading:  'status.uploading',
  processing: 'status.processing',
  ready:      'status.ready',
  failed:     'status.failed',
  pending:    'status.pending',
  accepted:   'status.accepted',
  expired:    'status.expired',
  revoked:    'status.revoked',
  draft:      'status.draft',
  submitted:  'status.submitted',
};
